import React, { useState, useEffect, useMemo } from "react";
import {
  X,
  Search,
  FileText,
  Check,
  Loader,
  Music,
  User,
  FolderOpen,
  Tag,
  ListMusic,
  ChevronLeft,
} from "lucide-react";
import { getAllScores, getAllSetlists } from "../../utils/scoreStorage";

const VIEW_MODES = [
  { id: "all", label: "All", icon: Music },
  { id: "composer", label: "Composer", icon: User },
  { id: "folder", label: "Folder", icon: FolderOpen },
  { id: "tags", label: "Tags", icon: Tag },
  { id: "setlists", label: "Setlists", icon: ListMusic },
];

function compareTitle(a, b) {
  return (a.title || "").localeCompare(b.title || "", undefined, {
    sensitivity: "base",
  });
}

function groupBy(items, keyFn, fallback) {
  const map = {};
  items.forEach((item) => {
    const raw = keyFn(item);
    const key = (typeof raw === "string" ? raw.trim() : "") || fallback;
    (map[key] ||= []).push(item);
  });
  return map;
}

function sortedGroupKeys(groups, fallback) {
  return Object.keys(groups).sort((a, b) => {
    if (a === fallback) return 1;
    if (b === fallback) return -1;
    return a.localeCompare(b, undefined, { sensitivity: "base" });
  });
}

export default function ScorePicker({
  userId,
  currentScoreId,
  onSelect,
  onClear,
  onClose,
}) {
  const [scores, setScores] = useState([]);
  const [setlists, setSetlists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("all");
  const [selectedTag, setSelectedTag] = useState(null);
  const [openSetlistId, setOpenSetlistId] = useState(null);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    Promise.all([getAllScores(userId), getAllSetlists(userId)])
      .then(([s, sl]) => {
        if (cancelled) return;
        setScores(s);
        setSetlists(sl);
      })
      .catch(() => {
        if (cancelled) return;
        setScores([]);
        setSetlists([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const scoresById = useMemo(() => {
    const map = {};
    scores.forEach((s) => {
      map[s.id] = s;
    });
    return map;
  }, [scores]);

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return scores;
    const q = searchQuery.toLowerCase();
    return scores.filter(
      (s) =>
        s.title?.toLowerCase().includes(q) ||
        s.composer?.toLowerCase().includes(q) ||
        s.folder?.toLowerCase().includes(q) ||
        s.tags?.some((t) => t.toLowerCase().includes(q))
    );
  }, [scores, searchQuery]);

  const sortedAlpha = useMemo(
    () => [...filtered].sort(compareTitle),
    [filtered]
  );

  const groupedByComposer = useMemo(
    () => groupBy(filtered, (s) => s.composer, "Unknown Composer"),
    [filtered]
  );

  const groupedByFolder = useMemo(
    () => groupBy(filtered, (s) => s.folder, "Unfiled"),
    [filtered]
  );

  const allTags = useMemo(
    () =>
      [...new Set(scores.flatMap((s) => s.tags || []))].sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: "base" })
      ),
    [scores]
  );

  const taggedScores = useMemo(() => {
    if (!selectedTag) return [];
    return [...filtered]
      .filter((s) => s.tags?.includes(selectedTag))
      .sort(compareTitle);
  }, [filtered, selectedTag]);

  const handleSelect = (score) => {
    onSelect(score.id, score.title);
    onClose();
  };

  const handleViewModeChange = (id) => {
    setViewMode(id);
    setSelectedTag(null);
    setOpenSetlistId(null);
  };

  const renderScoreRow = (score) => (
    <button
      key={score.id}
      onClick={() => handleSelect(score)}
      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left hover:bg-blue-50 transition-colors ${
        score.id === currentScoreId ? "bg-blue-50 ring-1 ring-blue-300" : ""
      }`}
    >
      <FileText
        size={18}
        className={
          score.id === currentScoreId
            ? "text-blue-500 flex-shrink-0"
            : "text-gray-400 flex-shrink-0"
        }
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-800 truncate">
          {score.title}
        </p>
        {score.composer && (
          <p className="text-xs text-gray-500 truncate">{score.composer}</p>
        )}
      </div>
      {score.id === currentScoreId && (
        <Check size={16} className="text-blue-500 flex-shrink-0" />
      )}
    </button>
  );

  const renderGroupHeader = (label) => (
    <h4 className="px-3 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
      {label}
    </h4>
  );

  const renderGrouped = (groups, fallback) => {
    const keys = sortedGroupKeys(groups, fallback);
    return keys.map((key) => (
      <div key={key} className="mb-1">
        {renderGroupHeader(key)}
        {[...groups[key]].sort(compareTitle).map(renderScoreRow)}
      </div>
    ));
  };

  const openSetlist = openSetlistId
    ? setlists.find((sl) => sl.id === openSetlistId)
    : null;

  const setlistScores = useMemo(() => {
    if (!openSetlist) return [];
    return (openSetlist.scoreIds || [])
      .map((id) => scoresById[id])
      .filter(Boolean);
  }, [openSetlist, scoresById]);

  const renderListBody = () => {
    if (loading) {
      return (
        <div className="flex justify-center py-8">
          <Loader size={20} className="animate-spin text-blue-500" />
        </div>
      );
    }

    if (scores.length === 0) {
      return (
        <p className="text-center text-sm text-gray-500 py-8">
          No scores uploaded yet. Upload scores from the Scores tab first.
        </p>
      );
    }

    if (viewMode === "setlists") {
      if (openSetlist) {
        if (setlistScores.length === 0) {
          return (
            <p className="text-center text-sm text-gray-500 py-8">
              This setlist is empty.
            </p>
          );
        }
        return setlistScores.map(renderScoreRow);
      }

      if (setlists.length === 0) {
        return (
          <p className="text-center text-sm text-gray-500 py-8">
            No setlists yet. Create one in the Scores tab.
          </p>
        );
      }

      return [...setlists]
        .sort((a, b) =>
          (a.name || "").localeCompare(b.name || "", undefined, {
            sensitivity: "base",
          })
        )
        .map((sl) => {
          const count = sl.scoreIds?.length || 0;
          return (
            <button
              key={sl.id}
              onClick={() => setOpenSetlistId(sl.id)}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left hover:bg-blue-50 transition-colors"
            >
              <ListMusic
                size={18}
                className="text-blue-500 flex-shrink-0"
              />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-800 truncate">
                  {sl.name}
                </p>
                <p className="text-xs text-gray-500">
                  {count} score{count !== 1 ? "s" : ""}
                </p>
              </div>
            </button>
          );
        });
    }

    if (filtered.length === 0) {
      return (
        <p className="text-center text-sm text-gray-500 py-8">
          {searchQuery.trim()
            ? `No matches for "${searchQuery}"`
            : "No scores match your criteria"}
        </p>
      );
    }

    if (viewMode === "all") {
      return sortedAlpha.map(renderScoreRow);
    }

    if (viewMode === "composer") {
      return renderGrouped(groupedByComposer, "Unknown Composer");
    }

    if (viewMode === "folder") {
      return renderGrouped(groupedByFolder, "Unfiled");
    }

    if (viewMode === "tags") {
      if (!selectedTag) {
        return (
          <p className="text-center text-sm text-gray-500 py-8">
            {allTags.length === 0
              ? "No tags yet. Add tags to your scores to filter here."
              : "Select a tag to view scores."}
          </p>
        );
      }
      if (taggedScores.length === 0) {
        return (
          <p className="text-center text-sm text-gray-500 py-8">
            No scores with this tag.
          </p>
        );
      }
      return taggedScores.map(renderScoreRow);
    }

    return null;
  };

  const showSearch = !(viewMode === "setlists" && !openSetlist);
  const showTagChips = viewMode === "tags" && allTags.length > 0;
  const showSetlistBackBar = viewMode === "setlists" && openSetlist;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold text-gray-800">Attach Score</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X size={18} />
          </button>
        </div>

        {/* View mode tabs */}
        <div className="flex gap-1 px-2 pt-3 pb-2 border-b">
          {VIEW_MODES.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => handleViewModeChange(id)}
              className={`flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors ${
                viewMode === id
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              <Icon size={11} />
              {label}
            </button>
          ))}
        </div>

        {/* Search */}
        {showSearch && (
          <div className="p-3 border-b">
            <div className="relative">
              <Search
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Search scores..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        )}

        {/* Tag filter chips */}
        {showTagChips && (
          <div className="flex flex-wrap gap-1.5 p-3 border-b max-h-28 overflow-y-auto">
            {allTags.map((tag) => (
              <button
                key={tag}
                onClick={() =>
                  setSelectedTag(selectedTag === tag ? null : tag)
                }
                className={`px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors ${
                  selectedTag === tag
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        )}

        {/* Setlist back bar */}
        {showSetlistBackBar && (
          <div className="flex items-center gap-2 px-3 py-2 border-b bg-gray-50">
            <button
              onClick={() => setOpenSetlistId(null)}
              className="p-0.5 rounded hover:bg-gray-200"
              aria-label="Back to setlists"
            >
              <ChevronLeft size={16} className="text-gray-600" />
            </button>
            <p className="text-sm font-medium text-gray-700 truncate">
              {openSetlist.name}
            </p>
          </div>
        )}

        {/* List */}
        <div className="flex-1 overflow-y-auto p-2">{renderListBody()}</div>

        {/* Footer */}
        {currentScoreId && (
          <div className="p-3 border-t">
            <button
              onClick={() => {
                onClear();
                onClose();
              }}
              className="w-full py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              Remove attached score
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
